// ─────────────────────────────────────────────────────────────────────────────
// useTargetInteraction — the shared tap / place / drag behaviour for every
// target face. Extracted verbatim from components/Target.jsx (#2063) so the
// single face canvas and its 17 face bindings all share one implementation.
//
// Coordinate frames (do NOT convert between them opportunistically):
//   • normalised — 1.0 = the outer edge of the WA target circle; what `arrows`
//     carry and what onTap/onArrowMove report.
//   • viewbox    — 0…240 with the centre at (120,120); what the SVG is drawn
//     in and what hitZone() scores against.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useRef, useState } from "react";
import { hitZone } from "../lib/scoring";
// Aliased: this module's local `vbX`/`vbY` are the resulting COORDINATES, not
// the conversion functions, and it returns them under those names.
import { toVbX, toVbY } from "../lib/targetGeometry";

export function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

/** Convert a DOM click event to normalised (x,y) and viewbox (vbX,vbY). */
function eventToCoords(e, svg) {
  const rect = svg.getBoundingClientRect();
  const cx = rect.width / 2;
  const cy = rect.height / 2;
  const px = e.clientX - rect.left - cx;
  const py = e.clientY - rect.top  - cy;
  // normalised coords: 1.0 = outer edge of the WA target
  const radius = Math.min(rect.width, rect.height) / 2;
  const x = clamp(px / radius, -1.18, 1.18);
  const y = clamp(py / radius, -1.18, 1.18);
  // viewbox coords (0–240, centre at 120,120)
  const vbX = toVbX(px / radius);
  const vbY = toVbY(py / radius);
  return { x, y, vbX, vbY };
}

// Touch must press-and-hold this long to lift an EXISTING arrow into drag
// mode. (A freshly placed arrow lifts after a much shorter 75 ms — grabbing
// the arrow you just put down is harmless.)
const LONG_PRESS_MS = 150;
// A mouse drag engages once the pointer has travelled this far (screen px) —
// a plain click never relocates an arrow. On touch the same distance is the
// hold's jitter tolerance: moving further before the hold elapses means the
// gesture is a placement swipe, not a grab.
const DRAG_THRESHOLD_PX = 11;
// Release-rewind (touch/pen): as the finger peels off the glass the contact
// patch rolls, emitting a few final noise moves that would land the arrow
// off-aim. On release the arrow settles at the newest position at least this
// old, discarding those lift-off samples.
const REWIND_MS = 80;
// How much drag history to keep for the rewind — comfortably > REWIND_MS.
const HISTORY_MS = 250;

/**
 * Shared interactive-target behaviour.
 *
 * A tap on the target places an arrow (`onTap`) — including a tap that lands
 * on an existing arrow's grab circle, since arrows legitimately group close
 * together. Relocating an already-placed arrow (`onArrowMove`) is deliberately
 * guarded so a glancing touch never moves it: on touch the arrow must be
 * pressed and held (LONG_PRESS_MS) to "lift" it into drag mode — touch-down
 * that immediately moves instead places a new arrow at the touch point and
 * drags that; with a mouse the drag engages only once the pointer crosses a
 * small movement threshold (and a plain click on an arrow does nothing).
 * While lifted, the marker is drawn offset clear of the finger (see
 * `ArrowMark`), and on touch release the arrow settles at its position
 * REWIND_MS before lift-off so the finger-peel flick never lands it off-aim.
 */
export function useTargetInteraction(face, { onTap, onArrowMove, interactive, arrowCount = 0 }) {
  const ref = useRef(null);
  // `dragIndex` is the lifted arrow — held in state so its marker re-renders
  // in the lifted, offset form.
  const [dragIndex, setDragIndex] = useState(null);
  // Gesture mechanics that must not trigger a re-render on every move.
  const g = useRef({
    index: null,
    pointerType: null,
    startX: 0,
    startY: 0,
    lifted: false,
    timer: null,
    swallowClick: false,
    newArrow: false,
    downPos: null, // {x, y, score} at touch-down on an existing arrow
    baseCount: 0, // arrowCount at gesture start — index a new arrow would get
    history: [], // applied drag positions {x, y, score, t} for release-rewind
  });

  function clearTimer() {
    if (g.current.timer != null) {
      clearTimeout(g.current.timer);
      g.current.timer = null;
    }
  }

  // #2014 — a pending lift timer must die with the component. Without this,
  // a pointer-down with no pointer-up (a mid-gesture unmount, or a test that
  // ends after tapping) left the 75 ms / 150 ms timer live; it then called
  // setDragIndex on an unmounted component — and in the test suite, after the
  // jsdom environment was torn down ("ReferenceError: window is not defined",
  // charged to whichever spec file was running at the time).
  useEffect(() => clearTimer, []);

  // Engage the drag — the arrow now follows the pointer.
  function lift() {
    clearTimer();
    if (g.current.index == null) return;
    g.current.lifted = true;
    setDragIndex(g.current.index);
    // A short tick confirms the hold registered, as OS drag-and-drop does.
    navigator.vibrate?.(12);
  }

  function handleClick() {
    if (!interactive) return;
    // Placement now happens in handleSVGPointerDown — this only swallows
    // the click that follows any pointer-down on the target or an arrow.
    if (g.current.swallowClick) {
      g.current.swallowClick = false;
    }
  }

  // Pointer-down on the SVG background (arrows stop propagation so this
  // only fires for empty-space taps). Places the arrow immediately and sets
  // up for instant drag — any movement grabs the new arrow without a hold.
  function handleSVGPointerDown(e) {
    if (!interactive || !onTap) return;
    const { x, y, vbX, vbY } = eventToCoords(e, ref.current);
    onTap({ x, y, score: hitZone(face, vbX, vbY) });
    ref.current?.setPointerCapture?.(e.pointerId);
    g.current = {
      index: arrowCount,
      pointerType: e.pointerType || "mouse",
      startX: e.clientX,
      startY: e.clientY,
      lifted: false,
      // A just-placed arrow lifts after a short 75 ms OR on any movement —
      // grabbing it is harmless, so no LONG_PRESS_MS guard is needed here.
      timer: setTimeout(lift, 75),
      swallowClick: true,
      newArrow: true,
      downPos: null,
      baseCount: arrowCount,
      history: [],
    };
  }

  function startArrowDrag(index, e) {
    if (!interactive || !onArrowMove) return;
    e.stopPropagation();
    ref.current?.setPointerCapture?.(e.pointerId);
    const pointerType = e.pointerType || "mouse";
    // On touch/pen, remember where the finger landed: a quick tap or an
    // immediate swipe places a NEW arrow at this point (arrows legitimately
    // land close together), while press-and-hold grabs the existing one.
    let downPos = null;
    if (pointerType !== "mouse" && onTap) {
      const { x, y, vbX, vbY } = eventToCoords(e, ref.current);
      downPos = { x, y, score: hitZone(face, vbX, vbY) };
    }
    g.current = {
      index,
      pointerType,
      startX: e.clientX,
      startY: e.clientY,
      lifted: false,
      timer: null,
      swallowClick: true,
      newArrow: false,
      downPos,
      baseCount: arrowCount,
      history: [],
    };
    // Touch / pen lift on a press-and-hold; a mouse lifts on movement.
    if (pointerType !== "mouse") {
      g.current.timer = setTimeout(lift, LONG_PRESS_MS);
    }
  }

  function handlePointerMove(e) {
    const st = g.current;
    if (st.index == null) return;
    if (!st.lifted) {
      const dist = Math.hypot(e.clientX - st.startX, e.clientY - st.startY);
      if (st.newArrow) {
        // Freshly placed arrow — enter drag on minimal movement (no hold needed).
        if (dist >= 4) lift();
      } else if (st.pointerType === "mouse") {
        if (dist >= DRAG_THRESHOLD_PX) lift();
      } else if (dist >= DRAG_THRESHOLD_PX) {
        // Touch moving off an existing arrow before the hold elapses: the
        // user is placing, not grabbing. Place a NEW arrow at the original
        // touch point and lift it so it follows the finger like a fresh one.
        clearTimer();
        if (!st.downPos) { st.index = null; return; }
        onTap(st.downPos);
        st.index = st.baseCount;
        st.newArrow = true;
        st.downPos = null;
        lift();
      } else {
        return; // still within the hold's jitter tolerance
      }
    }
    if (!st.lifted) return;
    const { x, y, vbX, vbY } = eventToCoords(e, ref.current);
    const pos = { x, y, score: hitZone(face, vbX, vbY) };
    onArrowMove(st.index, pos);
    // Record the applied position for release-rewind, pruning old samples.
    const t = performance.now();
    st.history.push({ ...pos, t });
    while (st.history.length && st.history[0].t < t - HISTORY_MS) st.history.shift();
  }

  function handlePointerUp(e) {
    const st = g.current;
    clearTimer();
    // Deliberate gesture endings only — pointercancel just resets state.
    if (st.index != null && e?.type === "pointerup" && st.pointerType !== "mouse") {
      if (st.lifted && st.history.length) {
        // Release-rewind: settle at the newest sample at least REWIND_MS old,
        // discarding the lift-off "flick" moves. A drag younger than the
        // window rewinds as far as possible — to its oldest sample.
        const cutoff = performance.now() - REWIND_MS;
        let sample = st.history[0];
        for (let i = st.history.length - 1; i >= 0; i--) {
          if (st.history[i].t <= cutoff) { sample = st.history[i]; break; }
        }
        onArrowMove(st.index, { x: sample.x, y: sample.y, score: sample.score });
      } else if (!st.lifted && !st.newArrow && st.downPos) {
        // Quick tap on an existing arrow before the hold elapsed: place a
        // NEW arrow at the touch point. (A mouse click still does nothing.)
        onTap(st.downPos);
      }
    }
    st.index = null;
    st.lifted = false;
    st.pointerType = null;
    st.newArrow = false;
    st.downPos = null;
    st.history = [];
    setDragIndex(null);
  }

  return {
    ref,
    dragIndex,
    face,
    handleClick,
    handleSVGPointerDown,
    startArrowDrag,
    handlePointerMove,
    handlePointerUp,
  };
}
